import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChatCircle, UserCircle } from '@phosphor-icons/react';
import type { Post } from '@researcher/shared';
import { useAuth } from '../../contexts/AuthContext';
import TopicBadge from './TopicBadge';
import HeartButton from './HeartButton';
import SaveButton from './SaveButton';
import PostCardMenu from './PostCardMenu';
import LoginModal from '../LoginModal';
import ReportModal from './ReportModal';
import { deletePost } from '../../utils/community';

interface PostCardProps {
  post: Post;
  onDeleted?: (postId: string) => void;
}

function timeAgo(date: any): string {
  const d = date?.toDate ? date.toDate() : new Date(date?._seconds ? date._seconds * 1000 : date);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 30) return `${diffDays}d ago`;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export default function PostCard({ post, onDeleted }: PostCardProps) {
  const { currentUser } = useAuth();
  const [isLoginOpen, setIsLoginOpen] = useState(false);
  const [showReport, setShowReport] = useState(false);
  const isAuthor = currentUser?.uid === post.authorId;
  const isRomy = post.systemGenerated === true;
  const displayName = isRomy ? 'Romy' : (post.anonymousDisplayName || post.anonymousAuthorHash?.substring(0, 8) || 'anonymous');

  const handleDelete = async () => {
    if (!confirm('Delete this post? This cannot be undone.')) return;
    try {
      await deletePost(post.id);
      onDeleted?.(post.id);
    } catch (err) {
      console.error('Failed to delete post:', err);
    }
  };

  return (
    <>
      <Link
        to={`/community/post/${post.id}`}
        className="block bg-white dark:bg-gray-900 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-4 sm:p-5 hover:shadow-md hover:border-pink-200 dark:hover:border-pink-800 transition-all"
      >
        {/* Header: avatar, username, topic, time, menu */}
        <div className="flex items-center gap-2 mb-2">
          <UserCircle size={24} className={isRomy ? 'text-pink-400 flex-shrink-0' : 'text-gray-400 dark:text-gray-500 flex-shrink-0'} weight="fill" />
          <span className={`text-sm font-medium ${isRomy ? 'text-pink-600 dark:text-pink-400' : 'text-gray-600 dark:text-gray-400'}`}>{displayName}</span>
          {isRomy && <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-pink-100 dark:bg-pink-900/30 text-pink-600 dark:text-pink-400">AI</span>}
          <span className="text-gray-300">·</span>
          <TopicBadge topicSlug={post.topicSlug} />
          <span className="text-gray-300">·</span>
          <span className="text-xs text-gray-400 dark:text-gray-500">{timeAgo(post.createdAt)}</span>
          {currentUser && (
            <div className="ml-auto">
              <PostCardMenu
                postId={post.id}
                isAuthor={isAuthor}
                onDelete={handleDelete}
                onReport={() => setShowReport(true)}
              />
            </div>
          )}
        </div>

        {/* Title */}
        <h3 className="text-base sm:text-lg font-semibold text-gray-800 dark:text-gray-100 mb-1 line-clamp-2">
          {post.title}
        </h3>

        {/* Body preview */}
        <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2 mb-3">
          {post.body}
        </p>

        {/* Footer: heart, comments, save */}
        <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400">
          <HeartButton
            postId={post.id}
            upvoteCount={post.upvoteCount}
            onLoginRequired={() => setIsLoginOpen(true)}
          />
          <div className="flex items-center gap-1.5">
            <ChatCircle size={18} />
            <span>{post.commentCount}</span>
          </div>
          <div className="ml-auto">
            <SaveButton
              postId={post.id}
              onLoginRequired={() => setIsLoginOpen(true)}
            />
          </div>
        </div>
      </Link>

      <LoginModal isOpen={isLoginOpen} onClose={() => setIsLoginOpen(false)} />
      {showReport && (
        <ReportModal
          isOpen={showReport}
          onClose={() => setShowReport(false)}
          postId={post.id}
        />
      )}
    </>
  );
}
